import orderBy from "lodash-es/orderBy";
import { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { isEqual, pick } from "lodash-es";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import PageWrapper from "components/PageWrapper";
import { InfoCircle } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import {
  PhaseName,
  adaptAllCommandsToFields,
  adaptFieldsToCommands,
  allPhaseHooks,
  phaseHooks,
  phases,
  toCommands,
} from "utils/hooks";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import Button from "ds/components/Button";
import useUpdateStack from "shared/Stack/useUpdateStack";
import Tooltip from "ds/components/Tooltip";

import { StackContext } from "../Context";
import StackHeader from "../components/Header";
import { getStacksBackUrl } from "../helpers";
import { showStackHooksDocumentationDrawer } from "./components/DocumentationDrawer";
import PhaseSection from "./components/PhaseSection";
import { ContextPhaseHooks, Phases, StackHooksFormData, StackPhaseHooks } from "./types";
import { StackWithHooks } from "./types";

type StackHooksContentProps = {
  stackWithHooks: StackWithHooks["stack"];
};

const StackHooksContent = ({ stackWithHooks }: StackHooksContentProps) => {
  const { stack, canUpdateStack } = useTypedContext(StackContext);

  const savedHooks = adaptFieldsToCommands(stackWithHooks.hooks);

  const form = useForm<StackHooksFormData>({
    defaultValues: {
      hooks: savedHooks,
    },
    mode: "onChange",
  });

  const { watch, handleSubmit } = form;

  const { stackUpdate, loading: updateLoading } = useUpdateStack({
    stack: { ...stack, hooks: stackWithHooks.hooks },
    refetchQueries: ["GetStackHooks"],
  });

  const hooksData = watch("hooks");
  const adaptedHooksFormData = adaptAllCommandsToFields(hooksData);
  const stackHooksData = pick(stackWithHooks.hooks, allPhaseHooks);
  const isDataChanged = !isEqual(stackHooksData, adaptedHooksFormData);

  useBlockNavigationModal({
    isNavigationBlocked: isDataChanged,
  });

  const onSave = useCallback(
    () =>
      handleSubmit((data) => {
        stackUpdate(adaptAllCommandsToFields(data.hooks));
      })(),
    [handleSubmit, stackUpdate]
  );

  const [stackHooks, contextHooks] = useMemo(() => {
    const stackHooks = {} as StackPhaseHooks;
    const contextHooks = {} as ContextPhaseHooks;

    for (const phase in phases) {
      const phaseName = phase as PhaseName;
      const [beforeHookName, afterHookName] = phaseHooks[phaseName];

      stackHooks[phaseName] = {
        before: beforeHookName ? toCommands(stackWithHooks?.hooks[beforeHookName]) : [],
        after: afterHookName ? toCommands(stackWithHooks?.hooks[afterHookName]) : [],
      };

      contextHooks[phaseName] = {
        before: [],
        after: [],
      };

      for (const context of stackWithHooks?.attachedContexts || []) {
        const processHook = (hook: string) => ({
          text: hook,
          contextId: context.contextId,
          contextName: context.contextName,
          isAutoattached: context.isAutoattached,
          priority: context.priority,
        });

        if (beforeHookName) {
          contextHooks[phaseName].before = orderBy(
            contextHooks[phaseName].before.concat(
              context.contextHooks[beforeHookName].map(processHook)
            ),
            ["isAutoattached", "priority", "contextName"],
            ["asc", "asc", "desc"]
          );
        }

        if (afterHookName) {
          contextHooks[phaseName].after = orderBy(
            contextHooks[phaseName].after.concat(
              context.contextHooks[afterHookName].map(processHook)
            ),
            ["isAutoattached", "priority", "contextName"],
            ["desc", "desc", "asc"]
          );
        }
      }
    }

    return [stackHooks, contextHooks];
  }, [stackWithHooks]);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stack.name,
    },
  ]);

  const handleOpenDocsDrawer = () => {
    showStackHooksDocumentationDrawer();
  };

  const processedPhases = useMemo(() => {
    const result: Phases = [];

    for (const [key, label] of Object.entries(phases)) {
      const name = key as PhaseName;
      const [beforeHookName, afterHookName] = phaseHooks[name];
      result.push({
        name,
        label,
        stackHooks: stackHooks[name],
        contextHooks: contextHooks[name],
        hasAfterHooks: afterHookName !== null,
        hasBeforeHooks: beforeHookName !== null,
      });
    }

    return result;
  }, [stackHooks, contextHooks]);

  return (
    <>
      <StackHeader />
      <PageInfoHeaderWrapper>
        <Box align="center" gap="medium">
          <PageInfoHeaderTitle title="Hooks" />
          <ButtonIcon icon={InfoCircle} onPress={handleOpenDocsDrawer} variant="ghost">
            Open information drawer
          </ButtonIcon>
        </Box>
        <PageInfoHeaderActions>
          {(canUpdateStack.result || !!canUpdateStack.message) && (
            <Tooltip
              active={!!canUpdateStack.message}
              on={(props) => (
                <Button
                  {...props}
                  variant="primary"
                  onPress={onSave}
                  loading={updateLoading}
                  disabled={!canUpdateStack.result || updateLoading}
                >
                  Save changes
                </Button>
              )}
            >
              {canUpdateStack.message}
            </Tooltip>
          )}
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <FormProvider {...form}>
        <PageWrapper gap="medium">
          {processedPhases.map((phase) => (
            <PhaseSection key={phase.name} {...phase} />
          ))}
        </PageWrapper>
      </FormProvider>
    </>
  );
};

export default StackHooksContent;
